// Copy for the /ourServices/my-mrf-app page (2026 refresh).
// Section order follows the JIRA brief:
// Hero → Podcast → Bridge → Tornado → Before/During/After → Be Ready video →
// Core capabilities → Differentiator → Privacy & Security → Final CTA.

import constants from "../../../../config/config";

// One primary action across the whole page.
export const TRIAL_LABEL = "Start your free 14-day trial";

// Hero opening paragraph, one sentence per line so it scans.
export const HERO_SCENARIOS = [
  { icon: "tornado", text: "A tornado forms while you're at work." },
  { icon: "flood", text: "A flood threatens your neighborhood while your children are at school." },
  { icon: "hurricane", text: "A hurricane forces an evacuation." },
  { icon: "wildfire", text: "A wildfire changes direction." },
  { icon: "accident", text: "A family member is involved in an accident." },
  { icon: "power", text: "The power goes out." },
  { icon: "noSignal", text: "Cellular service becomes unreliable." },
  { icon: "unexpected", text: "Or an emergency happens that you simply never expected." },
];

export const VIDEOS = {
  podcast: {
    label: "Watch: The Emergency You Never Think Will Happen",
    src: constants.MRF_APP_PODCAST_VIDEO_URL,
  },
  tornado: {
    label: "Watch: MYREDFOLDER® Tornado Scenario",
    src: constants.MRF_APP_TORNADO_VIDEO_URL,
  },
  beReady: {
    label: "Watch: Be Ready Before the Emergency",
    src: constants.MRF_APP_BE_READY_VIDEO_URL,
  },
};

export const BRIDGE_DISRUPTIONS = [
  { icon: "groups", text: "Your family may be separated." },
  { icon: "block", text: "Roads may be closed." },
  { icon: "power", text: "Electricity may be unavailable." },
  { icon: "medication", text: "Someone may need medication." },
  { icon: "evacuate", text: "You may need to evacuate." },
  { icon: "folderOff", text: "Important documents may be inaccessible." },
  {
    icon: "hospital",
    text: "You may need to locate a hospital, police station, fire department, shelter, or another emergency resource.",
  },
];

export const TORNADO_SCENE = [
  "One parent is at work.",
  "Another is shopping.",
  "One child is home.",
  "Two others are leaving school.",
];

export const STAGES = [
  {
    key: "before",
    stage: "Before",
    title: "Prepare",
    lead: null,
    items: [
      "Build your personalized Family Emergency Action Plan.",
      "Add family members and pets.",
      "Establish emergency contacts.",
      "Select in-town and out-of-town meeting locations.",
      "Identify work, school, and frequently visited locations.",
      "Prepare hazard-specific checklists.",
      "Organize medications and important information.",
      "Create your home inventory and securely organize critical documents.",
    ],
    body: [],
    tagline: "Prepare once. Keep your plan current.",
  },
  {
    key: "during",
    stage: "During",
    title: "Respond",
    lead: "When an emergency happens, MYREDFOLDER® helps put your plan into action.",
    items: [
      "Access critical emergency information.",
      "Communicate with family members.",
      "Use safety and emergency features.",
      "Reference meeting locations and emergency contacts.",
      "Receive applicable alerts.",
      "Locate emergency services.",
      "Access important information available through the platform.",
    ],
    body: [],
    tagline: "Less searching. Less uncertainty. More informed action.",
  },
  {
    key: "after",
    stage: "After",
    title: "Recover",
    lead: "The emergency may end long before the recovery does.",
    items: [],
    body: [
      "MYREDFOLDER® helps you keep important records, insurance information, property documentation, home-inventory information, photographs, contacts, and other recovery-related information organized and accessible when you may need it most.",
    ],
    tagline: "Because being prepared for an emergency also means being prepared for what comes next.",
  },
];

export const BE_READY_BEFORE = [
  "Before the warning.",
  "Before the evacuation.",
  "Before the power goes out.",
  "Before roads become impassable.",
];

export const FAMILY_QUESTIONS_SHORT = ["Where are you?", "Are you safe?", "Where should we meet?", "What do we do now?"];

export const CAPABILITIES = [
  {
    icon: "plan",
    title: "My Emergency Action Plan",
    body: "Build a personalized household emergency plan around the people and places that matter most—family members, emergency contacts, pets, meeting locations, schools, workplaces, medications, frequently visited locations, and your trusted support network.",
  },
  {
    icon: "checkin",
    title: "Family Safety & Check-In",
    body: "Help your household coordinate when family members are separated. MYREDFOLDER® brings family preparedness, safety status, communication, and emergency-plan information into one connected experience.",
  },
  {
    icon: "sos",
    title: "I Need Help Now",
    body: "When a situation requires immediate attention from your designated MYREDFOLDER® safety network, activate emergency functionality designed to quickly communicate that assistance may be needed.",
  },
  {
    icon: "alerts",
    title: "Emergency Alerts",
    body: "Keep important hazard and emergency information closer to the same place where your family maintains its preparedness plan.",
  },
  {
    icon: "hospital",
    title: "Emergency Services Locations",
    body: "Quickly identify important nearby emergency resources when location services and connectivity are available.",
  },
  {
    icon: "place",
    title: "Meeting Locations",
    body: "Establish both local and out-of-town meeting locations before an emergency separates your household or forces an evacuation.",
  },
  {
    icon: "contacts",
    title: "Emergency Contacts",
    body: "Keep the people your family may need to reach organized and accessible as part of your Emergency Action Plan.",
  },
  {
    icon: "pets",
    title: "Family Pets",
    body: "Include pets in your preparedness planning with the information your household may need to care for and evacuate them safely.",
  },
  {
    icon: "medication",
    title: "Medication Information",
    body: "Organize important medication information so critical details are easier to access when normal routines are disrupted.",
  },
  {
    icon: "checklist",
    title: "Emergency Checklists",
    body: "Turn preparedness into action with organized checklists that help your household prepare for relevant hazards and track what still needs to be completed.",
  },
  {
    icon: "vault",
    title: "Document Vault",
    body: "Organize important documents and records so you aren't searching through filing cabinets—or trying to remember where something is stored—after an emergency.",
  },
  {
    icon: "inventory",
    title: "Home Inventory",
    body: "Document your belongings before a loss occurs, helping preserve information that may become important during insurance and disaster recovery.",
  },
];

// Keep this wording conservative until testing confirms which modules work offline.
export const OFFLINE_CAPABILITY = {
  icon: "offline",
  title: "Offline Preparedness",
  status: "In development",
  body: "MYREDFOLDER® is being designed so selected critical information can remain available when connectivity is limited, because disasters are precisely when networks may be least dependable.",
};

export const OTHER_APPS = [
  "There are apps that warn you about weather.",
  "There are apps that store files.",
  "There are apps that share locations.",
  "There are apps that create checklists.",
];

export const FAMILY_QUESTIONS = [
  "Who needs to be accounted for?",
  "Where could everyone be?",
  "Where will you meet?",
  "Who should you contact?",
  "What information will you need?",
  "What happens if you have to evacuate?",
  "What happens if you cannot immediately get home?",
  "What will you need after the emergency?",
];

export const FINAL_UNKNOWNS = [
  "You may not know when the next emergency will happen.",
  "You may not know where you'll be.",
  "You may not know whether your family will be together.",
];

export const FINAL_ACTIONS = [
  "Create the plan.",
  "Connect your family.",
  "Organize the information that matters.",
  "Prepare for the hazards around you.",
  "Know where to meet.",
  "Know who to contact.",
  "Know where your important information is.",
];
